import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export type ProfileTab = 'profile' | 'wallet' | 'bridge' | 'history';

interface TabPersistenceState {
  lastViewedTab: ProfileTab;
  walletFilters?: any;
  historyFilters?: any;
}

const STORAGE_KEY = 'profile-tab-state';

export const useTabPersistence = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse current tab from URL
  const getCurrentTabFromUrl = useCallback((): ProfileTab => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    // Check query parameter first
    if (tabParam === 'wallet') return 'wallet';
    if (tabParam === 'bridge') return 'bridge';
    if (tabParam === 'history') return 'history';

    // Fall back to path-based detection
    if (path.includes('/profile/wallet')) return 'wallet';
    if (path.includes('/profile/bridge')) return 'bridge';
    if (path.includes('/profile/history')) return 'history';
    return 'profile';
  }, [location.pathname, location.search]);

  const [activeTab, setActiveTab] = useState<ProfileTab>(
    getCurrentTabFromUrl()
  );

  // Load persisted state from localStorage
  const loadPersistedState = (): TabPersistenceState => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load persisted tab state:', error);
    }
    return { lastViewedTab: 'profile' };
  };

  // Save state to localStorage
  const savePersistedState = (state: TabPersistenceState) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save tab state:', error);
    }
  };

  // Handle tab change with URL update and persistence
  const handleTabChange = (tab: ProfileTab) => {
    // Avoid redundant state updates and navigation if tab is unchanged
    if (tab === activeTab) {
      return;
    }

    setActiveTab(tab);

    // Update URL with query parameter only if it actually changes
    const basePath = '/profile';
    const newPath = tab === 'profile' ? basePath : `${basePath}?tab=${tab}`;
    const currentPath = `${basePath}${location.search ? location.search : ''}`;
    if (newPath !== currentPath) {
      navigate(newPath, { replace: true });
    }

    // Persist tab selection
    const currentState = loadPersistedState();
    savePersistedState({
      ...currentState,
      lastViewedTab: tab,
    });
  };

  // Persist filter state for specific tabs
  const persistFilters = (tab: ProfileTab, filters: any) => {
    const currentState = loadPersistedState();
    const filterKey = tab === 'wallet' ? 'walletFilters' : 'historyFilters';

    savePersistedState({
      ...currentState,
      [filterKey]: filters,
    });
  };

  // Load persisted filters for a specific tab
  const loadPersistedFilters = (tab: ProfileTab) => {
    const state = loadPersistedState();
    return tab === 'wallet' ? state.walletFilters : state.historyFilters;
  };

  // Initialize tab from URL or persisted state on mount
  useEffect(() => {
    const urlTab = getCurrentTabFromUrl();

    // If URL has a specific tab (not just /profile), use it and save to localStorage
    if (urlTab !== 'profile') {
      if (urlTab !== activeTab) {
        setActiveTab(urlTab);
      }

      // Update persisted state to match URL
      const currentState = loadPersistedState();
      savePersistedState({
        ...currentState,
        lastViewedTab: urlTab,
      });
    } else if (location.pathname === '/profile') {
      // Default to profile tab
      if (activeTab !== 'profile') {
        setActiveTab('profile');
      }
    }
  }, [
    location.pathname,
    location.search,
    navigate,
    getCurrentTabFromUrl,
    activeTab,
  ]);

  // Handle browser back/forward navigation or query-only changes
  useEffect(() => {
    const urlTab = getCurrentTabFromUrl();
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [location.pathname, location.search, activeTab, getCurrentTabFromUrl]);

  return {
    activeTab,
    handleTabChange,
    persistFilters,
    loadPersistedFilters,
  };
};
